import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useSEO } from "../../context/SEOContext";
import Swal from "sweetalert2";
import AddtoCart from "../../components/AddtoCart/AddtoCart";
import "./ProductDetail.css";
import { useCurrency } from "./../../components/PriceFormat/PriceFormat";
import { useTranslation } from "react-i18next";
import { loadProductsWithLangFallback } from "../../utils/productLoader";
import Artboard1 from "./../../assets/perfume/artboard1.webp";
import Artboard2 from "./../../assets/perfume/artboard2.webp";
import Artboard3 from "./../../assets/perfume/artboard3.webp";
import ARlogo from "./../../assets/arlogo.png";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  FreeMode,
  Pagination,
  Mousewheel,
  Keyboard,
  Navigation,
  Zoom,
} from "swiper/modules";
import InstallmentData from "../../api/InstallmentOptions";
import RenderStars from "../../components/renderStars/renderStars";
import PerfumeCustomization from "../../components/PerfumeCustomization/PerfumeCustomization";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import axios from "axios";
import { pickDisplayPrice, formatMoney } from "../../utils/pricePicker";

// --- STOCK HELPERS ---
const norm = (v) =>
  String(v || "")
    .trim()
    .toLowerCase();
const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const OUT_SET = new Set([
  "out of stock",
  "out_of_stock",
  "outofstock",
  "stokta yok",
  "yok",
  "0",
]);

const isVariantOut = (variant) =>
  toNum(variant?.stock_quantity) <= 0 ||
  OUT_SET.has(norm(variant?.availability));

const isProductOut = (p) =>
  toNum(p?.stock_quantity) <= 0 || OUT_SET.has(norm(p?.availability));

const NoteBlock = ({ image, title, notes }) => (
  <div className="d-flex align-items-center mt-4">
    <img
      src={image + "?v=123456"}
      alt={title}
      className="me-3 artbmoardsize2"
    />
    <div>
      <p className="mb-1 fw-semibold">{title}</p>
      <p className="mb-0 text-muted">{notes}</p>
    </div>
  </div>
);

const ProductDetail = ({ $product }) => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true); // ✅ yükleniyor mu?
  const [notFound, setNotFound] = useState(false); // ✅ ürün hiç bulunamadı mı?

  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [zoomPositions, setZoomPositions] = useState({});
  const [reviews, setReviews] = useState([]);

  const [name, setName] = useState("");
  const [rating, setRating] = useState("");
  const [comment, setComment] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const swiperRef = useRef(null);

  const [customization, setCustomization] = useState({
    enabled: null, // null = henüz seçilmedi
    text: "",
  });
  const [customizationError, setCustomizationError] = useState("");

  const setSeo = useSEO();

  useEffect(() => {
    if (product) {
      setSeo({
        title: `${product.name} | Mükemel`,
        description: product.metadesc || "",
        keywords: product.metakey || "",
      });
    }
  }, [product, setSeo]); // küçük iyileştirme

  const { currency } = useCurrency();

  const selectedColorName = selectedColor ?? null;

  const picked = useMemo(() => {
    if (!product) return null;

    const isPerfume =
      Array.isArray(product.category) && product.category.includes("Perfume");

    // Parfüm manual_prices önceliği (kalsın)
    if (isPerfume && product.manual_prices) {
      const val = product.manual_prices?.[currency];
      const n = Number(val);
      if (val != null && !Number.isNaN(n)) {
        return { amount: n, curr: currency };
      }
    }

    // ✅ varyasyon / fallback fiyat
    return pickDisplayPrice(product, currency, selectedColorName);
  }, [product, currency, selectedColorName]);

  const selectedVariant = useMemo(() => {
    if (!product?.color_images?.length || !selectedColor) return null;
    return product.color_images.find((c) => c.name === selectedColor) || null;
  }, [product, selectedColor]);

  const isOutOfStock = useMemo(() => {
    if (!product) return true;
    if (product.color_images?.length > 0) {
      return !selectedVariant || isVariantOut(selectedVariant);
    }
    return isProductOut(product);
  }, [product, selectedVariant]);

  // ✅ ÜRÜN VERİSİNİ ÇEK (loading & notFound kontrolü ile)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setNotFound(false);

        const allProducts = await loadProductsWithLangFallback(i18n.language);
        const found = allProducts.find((p) => String(p.id) === String(id));

        if (found) {
          setProduct(found);
          if (found.color_images?.length > 0) {
            const firstInStock =
              found.color_images.find((v) => !isVariantOut(v)) ||
              found.color_images[0];
            setSelectedColor(firstInStock?.name || null);
          }
        } else {
          console.error("Product not found");
          setProduct(null);
          setNotFound(true); // ✅ ürün yok
        }
      } catch (error) {
        console.error("Error fetching product data:", error);
        setProduct(null);
        setNotFound(true); // ✅ hata da ürün yok gibi davranıyoruz
      } finally {
        setLoading(false); // ✅ her durumda loading false
      }
    };

    fetchData();
  }, [id, i18n.language]);

  // ✅ review çekme – dependency'den reviews çıkarıldı (sonsuz döngü olmasın)
  useEffect(() => {
    if (!id) return;
    axios
      .get(`${process.env.REACT_APP_API_URL}/review?action=list`)
      .then((res) => {
        const activeReviews = res.data.filter(
          (item) => item.status === "1" && item.product_id === id
        );
        setReviews(activeReviews);
      })
      .catch((err) => {
        console.error("Yorumlar alınamadı:", err);
      });
  }, [id]);

  const handleToggleDescription = () => setIsExpanded((v) => !v);
  const handleColorChange = (color) => setSelectedColor(color);

  const getImages = () => {
    if (selectedColor) {
      const colorImages = product.color_images.find(
        (c) => c.name === selectedColor
      )?.images;
      return colorImages || product.default_images;
    }
    return product.default_images;
  };

  useEffect(() => {
    const syncWishlist = () => {
      const stored = JSON.parse(localStorage.getItem("wishlistItems")) || [];
      setWishlistItems(stored);
    };
    syncWishlist();
    window.addEventListener("storage", syncWishlist);
    window.addEventListener("wishlistUpdated", syncWishlist);
    return () => {
      window.removeEventListener("storage", syncWishlist);
      window.removeEventListener("wishlistUpdated", syncWishlist);
    };
  }, []);

  const handleWishlistClick = (e) => {
    e.preventDefault();
    const key = selectedColor ? `${product.id}-${selectedColor}` : product.id;
    if (wishlistItems.includes(key)) {
      const updated = wishlistItems.filter((item) => item !== key);
      setWishlistItems(updated);
      localStorage.setItem("wishlistItems", JSON.stringify(updated));
      Swal.fire({
        icon: "success",
        title: t("RemovedWishlistItems"),
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2000,
        background: "#fff",
        iconColor: "#dc3545",
      });
    } else {
      const updated = [...wishlistItems, key];
      setWishlistItems(updated);
      localStorage.setItem("wishlistItems", JSON.stringify(updated));
      Swal.fire({
        icon: "success",
        title: t("AddWishlistItems"),
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2000,
      });
    }
    window.dispatchEvent(new Event("wishlistUpdated"));
  };

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("wishlistItems")) || [];
    setWishlistItems(saved);
  }, []);

  // ✅ 1) YÜKLENİYORSA
  if (loading) {
    return (
      <section className="product-detail">
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-6 text-center">
              <div className="alert alert-info" role="alert">
                {t("ProductLoading")}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ✅ 2) ÜRÜN BULUNAMADIYSA (ID YANLIŞ / ÜRÜN SİLİNMİŞ)
  if (notFound || !product) {
    return (
      <section className="product-detail">
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-6 text-center">
              <div className="alert alert-danger" role="alert">
                {t("ProductNotFound")}
              </div>
              <Link
                to="/shop"
                className="btn bgcolorfooterbottom mt-3 pe-5 px-5"
              >
                {t("Shop")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ✅ 3) BURADAN SONRASI: ÜRÜN VARSA NORMAL SAYFA
  const shortDescription = product.description?.slice(0, 180) || "";
  const fullDescription = product.description || "";

  const handleMouseMove = (e, index) => {
    const { left, top, width, height } = e.target.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomPositions((prev) => ({ ...prev, [index]: `${x}% ${y}%` }));
  };
  const handleMouseLeave = (index) => {
    setZoomPositions((prev) => ({ ...prev, [index]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !rating || !comment) {
      Swal.fire({
        icon: "warning",
        title: t("missingInformationTitle"),
        text: t("missingInformationText"),
        confirmButtonText: "OK",
      });
      return;
    }
    const result = await Swal.fire({
      title: t("submitReviewTitle"),
      text: t("submitReviewText"),
      icon: "question",
      showDenyButton: true,
      showCancelButton: false,
      confirmButtonText: t("confirmButton"),
      denyButtonText: t("denyButton"),
      confirmButtonColor: "#3085d6",
      denyButtonColor: "#d33",
    });
    if (result.isConfirmed) {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/review`,
          {
            product_id: id,
            name,
            rating,
            comment,
          }
        );
        if (res.status === 201) {
          Swal.fire({
            icon: "success",
            title: t("thankYouTitle"),
            text: t("thankYouText"),
          }).then(() => window.location.reload());
          setName("");
          setRating("");
          setComment("");
        }
      } catch (err) {
        console.error("Review submission failed:", err);
        Swal.fire({
          icon: "error",
          title: t("errorTitle"),
          text: t("errorText"),
        }).then(() => window.location.reload());
      }
    } else if (result.isDenied) {
      Swal.fire({
        icon: "info",
        title: t("cancelledTitle"),
        text: t("cancelledText"),
      }).then(() => window.location.reload());
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  const isPerfume = (product?.category ?? "").includes("Perfume");

  return (
    <section className="product-detail">
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-lg-7 pe-0 px-0">
            <div className="d-none d-sm-none d-md-block">
              {getImages().map((image, index) => (
                <div
                  key={index}
                  className="image-zoom-wrapper"
                  onMouseMove={(e) => handleMouseMove(e, index)}
                  onMouseLeave={() => handleMouseLeave(index)}
                >
                  <img
                    src={image + "?v=123456"}
                    alt={`ProductImage-${selectedColor || "default"}-${index}`}
                    className="w-100 bgcolordetail"
                  />
                  {typeof zoomPositions[index] === "string" && (
                    <div
                      className="zoomed-image"
                      style={{
                        backgroundImage: `url(${image})`,
                        backgroundPosition: zoomPositions[index],
                        opacity: 1,
                      }}
                    />
                  )}
                  {zoomPositions[index] === null && (
                    <div
                      className="zoomed-image"
                      style={{
                        backgroundImage: `url(${image})`,
                        backgroundPosition: "center center",
                        opacity: 0,
                      }}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="d-block d-sm-block d-md-none">
              <Swiper
                className="product-swiper detail-page-swiper"
                modules={[Pagination, Zoom]}
                slidesPerView={1}
                spaceBetween={12}
                pagination={{ clickable: true }}
                zoom={{ maxRatio: 3 }}
              >
                {getImages().map((image, index) => (
                  <SwiperSlide key={`m-${index}`}>
                    {/* Zoom için bu sarmalayıcı gerekli */}
                    <div className="swiper-zoom-container">
                      <img
                        src={image + "?v=123456"}
                        alt={`ProductImage-${
                          selectedColor || "default"
                        }-${index}`}
                        className="w-100 bgcolordetail"
                        loading="lazy"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
            {product.video_url && product.video_url.includes("youtube.com") && (
              <div className="ratio ratio-16x9">
                <iframe
                  src={`https://www.youtube.com/embed/${new URL(
                    product.video_url
                  ).searchParams.get("v")}`}
                  title="YouTube video"
                  allowFullScreen
                ></iframe>
              </div>
            )}

            {/* <ModelViewer
              src="https://apimukemel.com/api/assets/3DModel.glb"
              ar
              ar-modes="scene-viewer webxr quick-look"
              camera-controls
              shadow-intensity="1"
              exposure="1"
              alt="Ürün 3B önizleme"
              style={{ width: "100%", height: "60vh", maxHeight: 600 }}
            >
              <button slot="ar-button" className="ar-btn">
                AR’da Gör
              </button>
            </ModelViewer> */}
          </div>

          <div
            className="col-lg-5 py-5 text-dark p-5"
            style={{
              position: "sticky",
              top: "0",
              height: "100vh",
              overflowY: "auto",
            }}
          >
            <div className="row justify-content-center">
              <div className="col-lg-9">
                <div className="d-flex justify-content-between">
                  <span className="fs-10 fw-normal">{product.stock_code}</span>
                  <Link onClick={handleWishlistClick}>
                    <span
                      className={`${
                        wishlistItems.includes(
                          selectedColor
                            ? `${product.id}-${selectedColor}`
                            : product.id
                        )
                          ? "fas fa-heart wbtextcolor3"
                          : "far fa-heart wbtextcolor3"
                      } fa-lg`}
                    ></span>
                  </Link>
                </div>

                <h1 className="mb-0 fs-3 fw-normal">{product.name}</h1>

                {/* 🔸 FİYAT GÖSTERİMİ (tek yerden) */}
                {!isOutOfStock ? (
                  <p className="fw-normal fw-14 mt-1">
                    {picked
                      ? formatMoney(picked.amount, picked.curr, {
                          min: 0,
                          max: 0,
                        })
                      : "-"}{" "}
                    -{" "}
                    <a data-bs-toggle="modal" href="#InstallmentModalToggle">
                      <span className="badge wbbgcolor pe-3 px-3 pt-2 pb-2">
                        {t("InstallmentOptions")}
                      </span>
                    </a>
                  </p>
                ) : (
                  // OOS iken fiyatı GİZLE, sadece 'Out Of Stock' yaz
                  <p className="fw-bold fw-14 mt-1 wbtextcolor3">
                    {t("Out Of Stock")}
                  </p>
                )}

                {/* Color selection */}
                {product.color_images && product.color_images.length > 0 ? (
                  <>
                    <div className="d-flex justify-content-between">
                      <p className="fw-14 fw-500">{t("Color")}</p>
                      <p className="fw-normal fs-11">
                        {selectedColor || "Select a color"}
                      </p>
                    </div>

                    <div className="d-flex justify-content-start pt-0">
                      {product.color_images.map((colorOption, index) => (
                        <div
                          key={index}
                          className={`color-option me-3 ${
                            selectedColor === colorOption.name ? "active" : ""
                          }`}
                          onClick={() => handleColorChange(colorOption.name)}
                        >
                          <img
                            src={colorOption.images[0] + "?v=123456"}
                            width={70}
                            alt={colorOption.name}
                            className="color-image"
                            style={{
                              border:
                                selectedColor === colorOption.name
                                  ? "2px solid #741819"
                                  : "none",
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="d-flex justify-content-start pt-0">
                    {product.default_images.slice(0, 3).map((image, index) => (
                      <img
                        key={index}
                        src={image + "?v=123456"}
                        width={70}
                        alt={`Default-${index}`}
                        className="me-3  border-color-image"
                      />
                    ))}
                  </div>
                )}

                {Array.isArray(product?.category) &&
                  product.category.includes("Perfume") &&
                  // Customization fiyatı 0.00 değilse göster
                  (parseFloat(product?.customization_tr_usd) > 0 ||
                    parseFloat(product?.customization_intl_usd) > 0) && (
                    <PerfumeCustomization
                      value={customization}
                      onChange={(val) => {
                        setCustomization(val);
                        setCustomizationError(""); // Seçim yapıldığında hatayı temizle
                      }}
                      error={customizationError}
                    />
                  )}

                <hr />

                {!isPerfume && (
                  <>
                    <div className="d-flex justify-content-between">
                      <h2 className="fw-14 fw-500">{t("Material")}</h2>
                      <p className="fw-normal fs-11">
                        {product?.attributes?.material}
                      </p>
                    </div>
                  </>
                )}

                {!isOutOfStock ? (
                  // Parfüm + customization fiyatı > 0 + henüz seçim yapılmadıysa validation göster
                  isPerfume &&
                  (parseFloat(product?.customization_tr_usd) > 0 ||
                    parseFloat(product?.customization_intl_usd) > 0) &&
                  customization.enabled === null ? (
                    <button
                      className="btn fw-14 bgcolorfooterbottom w-100 p-2 fw-normal mb-1"
                      onClick={() => {
                        setCustomizationError(
                          t("customization.selectRequired") ||
                            "Lütfen bir seçim yapınız"
                        );
                      }}
                    >
                      <i className="fa-solid fa-cart-plus fa-1x me-2"></i>{" "}
                      {t("Add to Cart")}
                    </button>
                  ) : (
                    <AddtoCart
                      product={product}
                      variantName={selectedColor}
                      selectedColor={selectedColor}
                      customization={{
                        enabled: customization.enabled,
                        text: (customization.text || "").trim(),
                      }}
                    />
                  )
                ) : (
                  <button className="btn bgcolorfooterbottom2 text-white p-2 w-100">
                    {t("Out Of Stock")}
                  </button>
                )}

                <Link
                  to={`/ar/${product.id}`}
                  className="btn wbbgcolor2 mt-1 fs-11 w-100 p-2 fw-normal pb-1"
                >
                  <img className="me-1" src={ARlogo} width={27} alt="ar logo" />
                  {t("PlaceinAR")}
                </Link>

                <Link
                  to="/shop"
                  className="btn text-decoration-underline fs-11 rounded-pill w-100 p-2 fw-normal mb-3"
                >
                  {t("Find in Store")}
                </Link>

                {!product?.category?.includes("Perfume") ? (
                  // 1) Parfüm DEĞİLSE: genel ürün metni
                  <p className="fw-normal fs-11">
                    <i>{t("ProductDesc")}</i>
                    <br />
                    <a href="/installment-options" className="text-dark">
                      <i>{t("ProductInstallment")}</i>
                    </a>
                  </p>
                ) : // 2) Parfümse: 247/248 için uyarı, diğer parfümler için parfüm metni
                ["247", "248"].includes(String(product?.id).trim()) ? (
                  <div className="fw-bold fs-11 text-danger" role="alert">
                    <span>{t("privateperfumedesc1")}</span>
                    <br />
                    <span>{t("privateperfumedesc2")}</span>
                  </div>
                ) : (
                  <p className="fs-11 py-3">
                    <i>{t("PerfumeDesc")}</i>
                    <br />
                    <a href="/installment-options" className="text-dark">
                      <i>{t("ProductInstallment")}</i>
                    </a>
                  </p>
                )}

                <div className="mt-4">
                  <h2
                    className={`mb-0 fw-normal fs-11 ${
                      !isExpanded ? "truncated-text" : ""
                    }`}
                  >
                    {isExpanded ? fullDescription : shortDescription}
                  </h2>

                  {isExpanded && (
                    <div className="mt-4">
                      <h2 className="fw-normal fs-6">{t("Ingredıents")}</h2>
                      {product.category.includes("Perfume") && (
                        <div className="mt-3">
                          <NoteBlock
                            image={Artboard1}
                            title={t("Top")}
                            notes={t("WomenTop")}
                          />
                          <NoteBlock
                            image={Artboard2}
                            title={t("Middle")}
                            notes={t("WomenMiddle")}
                          />
                          <NoteBlock
                            image={Artboard3}
                            title={t("Base")}
                            notes={t("WomenBase")}
                          />
                          <p>{t("CompositionDetails")}</p>
                          <h2 className="fw-normal fs-6">{t("Directions")}</h2>
                          <p>{t("DirectionsDesc")}</p>
                          <h2 className="fw-normal fs-6">{t("Warning")}</h2>
                          <p className="mb-0">{t("warning1")}</p>
                          <p className="mb-0">{t("warning2")}</p>
                          <p className="mb-0">{t("warning3")}</p>
                          <p className="mb-0">{t("warning4")}</p>
                          <p className="mb-0">{t("warning5")}</p>
                          <p>{t("warning6")}</p>
                          <h2 className="fw-normal fs-6">
                            {t("Specifications")}
                          </h2>
                          {product?.category?.includes("Perfume") && (
                            <p className="mb-1">
                              *{" "}
                              {["247", "248"].includes(
                                String(product?.id).trim()
                              )
                                ? "5ML"
                                : "100ML"}
                            </p>
                          )}
                        </div>
                      )}
                      {!product.category.includes("Perfume") && (
                        <>
                          <p className="mb-0 fw-normal fs-11">
                            {product.attributes.dimensions}
                          </p>
                          <p className="fw-normal fs-11">
                            ({t("ProductSize")})
                          </p>
                        </>
                      )}

                      <div className="mb-3">
                        <p className="fw-normal fs-11 mb-1">
                          * {t("Quantity")}: {product.stock_quantity}
                        </p>
                        <p className="fw-normal fs-11 mb-1">
                          * {t("Category")}: {product.category.join(" , ")}
                        </p>
                        <p className="fw-normal fs-11 mb-1">
                          * {t("Color")}: {product.attributes.color}
                        </p>
                        <p className="fw-normal fs-11 mb-1">
                          * {t("Tags")}: {product.tags.join(" , ")}
                        </p>
                        {!product.category.includes("Perfume") && (
                          <p className="fw-normal fs-11 mb-1">
                            * {t("Weight")}: {product.attributes.weight}
                          </p>
                        )}
                        <p className="fw-normal fs-11 mb-1">
                          * {t("Brand")}: {product.attributes.brand}
                        </p>
                      </div>
                    </div>
                  )}

                  <button
                    className="btn btn-link p-0 text-dark fs-11"
                    onClick={handleToggleDescription}
                  >
                    {isExpanded ? t("ReadLess") : t("ReadMore")}
                  </button>
                </div>
              </div>

              <div className="col-lg-9 mt-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">{t("CustomerReviews")}</h5>
                  <button
                    type="button"
                    className="btn bgcolorfooterbottom fs-11"
                    data-bs-toggle="modal"
                    data-bs-target="#exampleModal"
                  >
                    <i className="fa-solid fa-plus fa-sm me-1"></i>
                    {t("AddReview")}
                  </button>
                </div>

                <Swiper
                  onSlideChange={(swiper) =>
                    setCurrentIndex(swiper.activeIndex)
                  }
                  onSwiper={(swiper) => (swiperRef.current = swiper)}
                  cssMode={true}
                  navigation={{ clickable: true }}
                  pagination={false}
                  mousewheel={true}
                  keyboard={true}
                  modules={[Navigation, Pagination, Mousewheel, Keyboard]}
                  className="mySwiper"
                >
                  {reviews.length > 0 ? (
                    reviews.map((review, index) => (
                      <SwiperSlide key={index}>
                        <div className="bordercolor p-3 rounded">
                          <div className="d-flex justify-content-between mt-2">
                            <div className="d-flex">
                              <img
                                src="https://apimukemel.com/api/assets/profile.png"
                                alt="User"
                                width={60}
                                height={60}
                                className="rounded me-2"
                              />
                              <div>
                                <h6 className="mb-1 mt-2">
                                  {review.name
                                    ?.split(" ")
                                    .map(
                                      (n) => n.charAt(0).toUpperCase() + "..."
                                    )
                                    .join(" ")}
                                </h6>
                                <RenderStars rating={parseInt(review.rating)} />
                              </div>
                            </div>
                            <div className="text-muted me-2">
                              {new Date(review.created_at).toLocaleDateString(
                                "tr-TR"
                              )}
                            </div>
                          </div>
                          <div className="mt-3" style={{ height: 70 }}>
                            <p className="mx-2">
                              {truncateText(review.comment, 230)}
                            </p>
                          </div>
                        </div>
                      </SwiperSlide>
                    ))
                  ) : (
                    <div
                      className="alert alert-danger text-center"
                      role="alert"
                    >
                      {t("noReviewsTitle")}
                    </div>
                  )}
                </Swiper>

                {reviews.length > 0 && (
                  <div className="text-center mt-2">
                    {currentIndex + 1} / {reviews.length}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Installment Modal */}
      <div
        className="modal fade"
        id="InstallmentModalToggle"
        aria-hidden="true"
        tabIndex="-1"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{t("InstallmentOptions")}</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>
            <div className="modal-body">
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-lg-12 pt-5 pb-3">
                    <Swiper
                      slidesPerView={3}
                      spaceBetween={30}
                      freeMode={true}
                      pagination={{ clickable: true }}
                      modules={[FreeMode, Pagination]}
                      className="mySwiper"
                      breakpoints={{
                        0: { slidesPerView: 1 },
                        640: { slidesPerView: 3 },
                        1024: { slidesPerView: 5 },
                      }}
                    >
                      {InstallmentData.blogs.map((item) => (
                        <SwiperSlide key={item.id} className="mb-5 text-center">
                          <img
                            src={item.images + "?v=123456"}
                            alt={item.alt}
                            width={170}
                            height={50}
                            style={{ objectFit: "contain" }}
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                  <div className="col-lg-8">
                    <table className="table border table-bordered text-center">
                      <thead>
                        <tr>
                          <th className="w-33 align-content-center">
                            {t("Taksit")}
                          </th>
                          <th className="w-33 align-content-center">
                            {t("TaksitOranı")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>1</td>
                          <td>{t("Ücretsiz!")}</td>
                        </tr>
                        <tr>
                          <td>2</td>
                          <td>{t("Ücretsiz!")}</td>
                        </tr>
                        <tr>
                          <td>3</td>
                          <td>{t("Ücretsiz!")}</td>
                        </tr>
                        <tr>
                          <td>6</td>
                          <td>%17,53</td>
                        </tr>
                        <tr>
                          <td>9</td>
                          <td>%25,94</td>
                        </tr>
                        <tr>
                          <td>12</td>
                          <td>%35,47</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn bgcolorfooterbottom text-white"
                data-bs-dismiss="modal"
              >
                {t("CloseButton")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      <div
        className="modal fade"
        id="exampleModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{t("ReviewComment")}</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("NameSurname")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <select
                    className="form-select"
                    value={rating}
                    onChange={(e) => setRating(e.target.value)}
                  >
                    <option value="">{t("Rating")}</option>
                    <option value="5">{t("Stars5")}</option>
                    <option value="4">{t("Stars4")}</option>
                    <option value="3">{t("Stars3")}</option>
                    <option value="2">{t("Stars2")}</option>
                    <option value="1">{t("Stars1")}</option>
                  </select>
                </div>
                <div className="mb-3">
                  <textarea
                    value={comment}
                    className="form-control"
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t("Comment")}
                  />
                </div>
                <button type="submit" className="btn bgcolorfooterbottom w-100">
                  <i className="fa-solid fa-plus fa-sm me-1"></i>
                  {t("AddReview")}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductDetail;
